import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag, ArrowRight, Tag } from "lucide-react";
import { http, formatErr } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { toast } from "sonner";

export default function Cart() {
  const { items, removeItem, updateQty, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setApplyingCoupon(true);
    try {
      const r = await http.post("/store/discount/validate", { code: couponCode.trim(), subtotal });
      setDiscount(r.data);
      toast.success(`Coupon "${couponCode}" applied!`);
    } catch (e) {
      toast.error(formatErr(e));
      setDiscount(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const discountAmount = discount
    ? discount.type === "percent"
      ? (subtotal * discount.value) / 100
      : Math.min(discount.value, subtotal)
    : 0;

  const total = Math.max(0, subtotal - discountAmount);

  if (items.length === 0) return (
    <div className="min-h-[60vh] grid place-items-center">
      <div className="text-center space-y-4">
        <ShoppingBag size={60} className="mx-auto text-gray-200" />
        <h2 className="text-2xl font-bold text-gray-800" style={{ fontFamily: "Cormorant Garamond, serif" }}>Your cart is empty</h2>
        <p className="text-gray-500 text-sm">Discover our curated collection and add items to your cart.</p>
        <Link to="/store/catalog" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-white" style={{ background: "#0F1E38" }}>
          Browse Products <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Cormorant Garamond, serif" }}>Your Cart ({items.length})</h1>
        <button onClick={clearCart} className="text-sm text-gray-400 hover:text-red-500 transition-colors">Clear cart</button>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Items */}
        <div className="lg:col-span-2 space-y-3">
          {items.map(item => (
            <div key={item.key} className="flex gap-4 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="w-20 h-20 rounded-xl overflow-hidden bg-gray-50 shrink-0">
                {item.product_image
                  ? <img src={item.product_image} alt={item.product_name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full grid place-items-center"><ShoppingBag size={24} className="text-gray-300" /></div>
                }
              </div>
              <div className="flex-1 min-w-0">
                <Link to={`/store/product/${item.product_id}`} className="font-semibold text-gray-900 hover:text-yellow-600 transition-colors leading-snug line-clamp-2">{item.product_name}</Link>
                {item.variant_label && <div className="text-xs text-gray-500 mt-0.5">{item.variant_label}</div>}
                <div className="mt-2 flex items-center gap-4">
                  <div className="flex items-center gap-1 border border-gray-200 rounded-xl overflow-hidden">
                    <button onClick={() => updateQty(item.key, item.qty - 1)} className="p-2 hover:bg-gray-50 transition-colors"><Minus size={13} /></button>
                    <span className="w-8 text-center text-sm font-bold">{item.qty}</span>
                    <button onClick={() => updateQty(item.key, item.qty + 1)} className="p-2 hover:bg-gray-50 transition-colors"><Plus size={13} /></button>
                  </div>
                  <span className="font-bold text-gray-900">${(item.price * item.qty).toFixed(2)}</span>
                  {item.qty > 1 && <span className="text-xs text-gray-400">${item.price.toFixed(2)} each</span>}
                </div>
              </div>
              <button onClick={() => removeItem(item.key)} className="p-1 text-gray-400 hover:text-red-500 transition-colors shrink-0">
                <X size={16} />
              </button>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
            <h2 className="font-bold text-gray-900 text-base">Order Summary</h2>

            {/* Coupon */}
            <div className="flex gap-2">
              <div className="flex-1 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                <Tag size={13} className="text-gray-400" />
                <input value={couponCode} onChange={e => setCouponCode(e.target.value)} placeholder="Discount code" className="text-sm bg-transparent outline-none flex-1" />
              </div>
              <button onClick={applyCoupon} disabled={applyingCoupon} className="px-4 py-2 rounded-xl text-sm font-bold text-white disabled:opacity-50" style={{ background: "#0F1E38" }}>
                Apply
              </button>
            </div>
            {discount && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-green-600 font-semibold">✓ {discount.code} applied</span>
                <button onClick={() => { setDiscount(null); setCouponCode(""); }} className="text-gray-400 hover:text-red-500"><X size={13} /></button>
              </div>
            )}

            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
              {discountAmount > 0 && <div className="flex justify-between text-green-600"><span>Discount{discount?.code ? ` (${discount.code})` : ""}</span><span>-${discountAmount.toFixed(2)}</span></div>}
              <div className="flex justify-between text-gray-600"><span>Shipping</span><span className="text-green-600">Calculated at checkout</span></div>
            </div>

            <div className="border-t border-gray-100 pt-3">
              <div className="flex justify-between font-bold text-lg text-gray-900">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>

            <button
              onClick={() => navigate("/store/checkout", { state: { discount, total } })}
              className="w-full py-4 rounded-2xl font-bold text-white text-center flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
              style={{ background: "#0F1E38" }}
            >
              Checkout <ArrowRight size={16} />
            </button>

            <div className="text-xs text-center text-gray-400">Secure checkout · Stripe & PayPal accepted</div>

            <Link to="/store/catalog" className="w-full block text-center text-sm text-gray-500 hover:text-yellow-600 transition-colors">← Continue Shopping</Link>
          </div>

          {/* Community note */}
          <div className="text-center text-xs text-gray-500 p-4 bg-yellow-50 rounded-2xl border border-yellow-100">
            <span className="font-semibold text-yellow-700">✞ Shop with peace</span><br />
            Your purchase supports the Celestial community and its mission.
          </div>
        </div>
      </div>
    </div>
  );
}
